// src/fields.ts
import { z } from "zod";
var FIELD_TYPES = [
  "text",
  "richText",
  "number",
  "boolean",
  "date",
  "select",
  "multiSelect",
  "media",
  "reference",
  "slug",
  "list"
];
var fieldKey = z.string().regex(/^[a-z][a-zA-Z0-9]*$/, "Field keys must be lowerCamelCase");
var base = z.object({
  label: z.string().optional(),
  help: z.string().optional(),
  required: z.boolean().default(false)
});
var fieldDefSchema = z.discriminatedUnion("type", [
  base.extend({ type: z.literal("text"), maxLength: z.number().int().positive().optional() }),
  base.extend({ type: z.literal("richText") }),
  base.extend({ type: z.literal("number"), min: z.number().optional(), max: z.number().optional() }),
  base.extend({ type: z.literal("boolean") }),
  base.extend({ type: z.literal("date"), includeTime: z.boolean().default(false) }),
  base.extend({ type: z.literal("select"), options: z.array(z.string()).min(1) }),
  base.extend({ type: z.literal("multiSelect"), options: z.array(z.string()).min(1) }),
  base.extend({
    type: z.literal("media"),
    accept: z.array(z.enum(["image", "video", "document"])).default(["image"])
  }),
  base.extend({ type: z.literal("reference"), to: z.string().min(1) }),
  base.extend({ type: z.literal("slug"), from: z.string().min(1) }),
  base.extend({
    type: z.literal("list"),
    of: z.record(fieldKey, z.lazy(() => fieldDefSchema))
  })
]);
var build = (type, opts) => fieldDefSchema.parse({ ...opts, type });
var fields = {
  text: (opts) => build("text", opts ?? {}),
  richText: (opts) => build("richText", opts ?? {}),
  number: (opts) => build("number", opts ?? {}),
  boolean: (opts) => build("boolean", opts ?? {}),
  date: (opts) => build("date", opts ?? {}),
  select: (opts) => build("select", opts),
  multiSelect: (opts) => build("multiSelect", opts),
  media: (opts) => build("media", opts ?? {}),
  reference: (opts) => build("reference", opts),
  slug: (opts) => build("slug", opts),
  list: (opts) => build("list", opts)
};
var asEnum = (options) => z.enum(options);
function valueSchemaFor(field) {
  const wrap = (schema) => field.required ? schema : schema.nullable().optional();
  switch (field.type) {
    case "text":
    case "richText":
    case "slug":
      return wrap(field.required ? z.string().min(1) : z.string());
    case "number":
      return wrap(z.number());
    case "boolean":
      return wrap(z.boolean());
    case "date":
      return wrap(z.string().datetime());
    case "select":
      return wrap(asEnum(field.options));
    case "multiSelect":
      return wrap(z.array(asEnum(field.options)));
    // Stores a media reference; a plain URL is still accepted so values
    // seeded before the media library keep validating — the delivery API
    // serves both as `MediaRef`.
    case "media": {
      const location = z.string().refine((value) => /^https?:\/\/./i.test(value) || /^\/(?!\/)/.test(value), "Enter a full https:// URL or a path starting with /");
      const media = wrap(
        z.union([z.object({ id: z.string().min(1), url: z.string().url().optional(), alt: z.string().optional() }), location])
      );
      return field.required ? media : z.preprocess((value) => value === "" ? null : value, media);
    }
    case "reference":
      return wrap(z.string().uuid());
    case "list": {
      const shape = {};
      for (const [key, subField] of Object.entries(field.of)) {
        shape[key] = valueSchemaFor(subField);
      }
      const itemSchema = z.object(shape);
      const arraySchema = z.array(itemSchema);
      return wrap(field.required ? arraySchema.min(1) : arraySchema);
    }
  }
}

// src/blocks.ts
import { z as z2 } from "zod";
var BLOCK_TYPES = [
  "heading",
  "text",
  "quote",
  "code",
  "bulletedList",
  "numberedList",
  "divider",
  "twoColumns",
  "embed",
  "customEmbed",
  "image"
];
var blockTypeSchema = z2.enum(BLOCK_TYPES);
var TEXT_BLOCK_TYPES = [
  "heading",
  "text",
  "quote",
  "code",
  "bulletedList",
  "numberedList"
];
var LIST_BLOCK_TYPES = ["bulletedList", "numberedList"];
var isTextBlock = (type) => TEXT_BLOCK_TYPES.includes(type);
var blockContentSchema = z2.discriminatedUnion("type", [
  z2.object({ type: z2.literal("heading"), text: z2.string().default("") }),
  z2.object({ type: z2.literal("text"), text: z2.string().default("") }),
  // `attribution` is who said it — optional, rendered as a <cite> after the quote.
  z2.object({ type: z2.literal("quote"), text: z2.string().default(""), attribution: z2.string().default("") }),
  z2.object({
    type: z2.literal("code"),
    text: z2.string().default(""),
    language: z2.string().default("text")
  }),
  z2.object({ type: z2.literal("bulletedList"), text: z2.string().default("") }),
  z2.object({ type: z2.literal("numberedList"), text: z2.string().default("") }),
  z2.object({ type: z2.literal("divider") }),
  z2.object({ type: z2.literal("twoColumns") }),
  z2.object({
    type: z2.literal("embed"),
    url: z2.string().default(""),
    provider: z2.string().optional()
  }),
  z2.object({ type: z2.literal("customEmbed"), markup: z2.string().default("") }),
  z2.object({
    type: z2.literal("image"),
    // Named `url`, not `mediaId` — there is no media library yet, so this is
    // the URL string itself, and `render.ts` (what the live site renders
    // through) reads `content.url`. The two must agree on the field name or
    // an image saved in the admin renders nothing on the public site.
    url: z2.string().nullable().default(null),
    alt: z2.string().default("")
  })
]);
var BUILT_IN_BLOCKS = [
  { type: "text", label: "Text", icon: "TextT", group: "basic", description: "Plain paragraph" },
  { type: "heading", label: "Heading", icon: "TextHOne", group: "basic", description: "Section heading" },
  { type: "bulletedList", label: "Bulleted list", icon: "ListBullets", group: "basic", description: "Unordered list item" },
  { type: "numberedList", label: "Numbered list", icon: "ListNumbers", group: "basic", description: "Ordered list item" },
  { type: "quote", label: "Quote", icon: "Quotes", group: "basic", description: "Pull quote with an accent rail" },
  { type: "divider", label: "Divider", icon: "Minus", group: "basic", description: "Horizontal rule" },
  { type: "code", label: "Code", icon: "Code", group: "advanced", description: "Monospaced code block" },
  { type: "twoColumns", label: "Two columns", icon: "Columns", group: "advanced", description: "Side-by-side block stacks" },
  { type: "image", label: "Image", icon: "Image", group: "media", description: "Image from the media library" },
  { type: "embed", label: "Embed", icon: "LinkSimple", group: "media", description: "URL with provider detection" },
  { type: "customEmbed", label: "Custom embed", icon: "BracketsAngle", group: "advanced", description: "Raw iframe, script or markup" }
];
function resolveBlockMeta(type, registry = []) {
  return BUILT_IN_BLOCKS.find((b) => b.type === type) ?? registry.find((b) => b.type === type) ?? {
    type,
    label: type,
    icon: "Cube",
    group: "project",
    description: "Custom block from your project"
  };
}

// src/custom-blocks.ts
import { z as z3 } from "zod";
var CUSTOM_BLOCK_PREFIX = "custom:";
var isCustomBlockType = (type) => type.startsWith(CUSTOM_BLOCK_PREFIX);
var customBlockKey = (type) => isCustomBlockType(type) ? type.slice(CUSTOM_BLOCK_PREFIX.length) : type;
var customBlockType = (key) => `${CUSTOM_BLOCK_PREFIX}${key}`;
var BLOCK_PROP_TYPES = ["text", "number", "boolean", "select", "media", "collection"];
var label = z3.string().optional();
var subPropDefSchema = z3.discriminatedUnion("type", [
  z3.object({ type: z3.literal("text"), label, default: z3.string().optional() }),
  z3.object({
    type: z3.literal("number"),
    label,
    default: z3.number().optional(),
    min: z3.number().optional(),
    max: z3.number().optional()
  }),
  z3.object({ type: z3.literal("boolean"), label, default: z3.boolean().optional() }),
  z3.object({
    type: z3.literal("select"),
    label,
    options: z3.array(z3.string()).min(1),
    default: z3.string().optional()
  }),
  z3.object({ type: z3.literal("media"), label })
]).superRefine((prop, ctx) => {
  if (prop.type === "select" && prop.default !== void 0 && !prop.options.includes(prop.default)) {
    ctx.addIssue({
      code: z3.ZodIssueCode.custom,
      path: ["default"],
      message: `Select default "${prop.default}" is not one of its options.`
    });
  }
  if (prop.type === "number" && prop.default !== void 0) {
    if (prop.min !== void 0 && prop.default < prop.min || prop.max !== void 0 && prop.default > prop.max) {
      ctx.addIssue({
        code: z3.ZodIssueCode.custom,
        path: ["default"],
        message: `Number default ${prop.default} is outside its min/max range.`
      });
    }
  }
});
var collectionPropDefSchema = z3.object({
  type: z3.literal("collection"),
  label,
  addLabel: z3.string().optional(),
  of: z3.record(fieldKey, subPropDefSchema),
  source: z3.object({
    collections: z3.array(z3.string().min(1)).min(1),
    defaultLimit: z3.number().int().min(1).max(20).default(5)
  }).optional()
});
var blockPropDefSchema = z3.union([
  subPropDefSchema,
  collectionPropDefSchema
]);
var blockKey = z3.string().regex(/^[a-z][a-z0-9-]*$/, "Block keys must be lowercase and hyphen-separated");
var blockDefSchema = z3.object({
  key: blockKey,
  name: z3.string().min(1),
  icon: z3.string().min(1).default("Cube"),
  description: z3.string().optional(),
  version: z3.string().min(1).default("1.0.0"),
  file: z3.string().optional(),
  props: z3.record(fieldKey, blockPropDefSchema)
});
function defineBlock(def) {
  return blockDefSchema.parse(def);
}
var mediaRefSchema = z3.object({ id: z3.string().min(1) });
var asEnum2 = (options) => z3.enum(options);
function subPropValueSchema(prop) {
  switch (prop.type) {
    case "text":
      return z3.string();
    case "number":
      return z3.number();
    case "boolean":
      return z3.boolean();
    case "select":
      return asEnum2(prop.options);
    case "media":
      return mediaRefSchema;
  }
}
var optional = (schema) => schema.nullable().optional();
function propValueSchema(prop) {
  if (prop.type !== "collection") return optional(subPropValueSchema(prop));
  const shape = {};
  for (const [key, sub] of Object.entries(prop.of)) shape[key] = optional(subPropValueSchema(sub));
  const rows = z3.array(z3.object(shape));
  if (!prop.source) return optional(rows);
  const binding = z3.object({
    collection: asEnum2(prop.source.collections),
    limit: z3.number().int().min(1).max(20)
  });
  return optional(
    z3.union([
      z3.object({ source: z3.literal("items"), items: rows }),
      z3.object({ source: binding })
    ])
  );
}
function blockPropsSchemaFor(def) {
  const shape = {};
  for (const [key, prop] of Object.entries(def.props)) shape[key] = propValueSchema(prop);
  return z3.object(shape);
}
function defaultSubValue(prop) {
  switch (prop.type) {
    case "text":
      return prop.default ?? "";
    case "number":
      return prop.default ?? null;
    case "boolean":
      return prop.default ?? false;
    case "select":
      return prop.default ?? null;
    case "media":
      return null;
  }
}
function defaultPropsFor(def) {
  const out = {};
  for (const [key, prop] of Object.entries(def.props)) {
    if (prop.type === "collection") {
      out[key] = prop.source ? { source: "items", items: [] } : [];
    } else {
      out[key] = defaultSubValue(prop);
    }
  }
  return out;
}
function defaultRowFor(prop) {
  const row = {};
  for (const [key, sub] of Object.entries(prop.of)) row[key] = defaultSubValue(sub);
  return row;
}
function blockMetaFromDef(def) {
  return {
    type: customBlockType(def.key),
    label: def.name,
    icon: def.icon,
    group: "project",
    description: def.description ?? "Custom block from your project"
  };
}
var customBlockContentSchema = z3.object({
  type: z3.string().refine(isCustomBlockType, "Not a custom block type"),
  props: z3.record(z3.unknown()).default({})
});
var BUILT_IN_BLOCK_TYPE_SET = new Set(BLOCK_TYPES);
function parseBlockContent(input) {
  const type = input.type;
  if (typeof type === "string" && isCustomBlockType(type)) {
    return customBlockContentSchema.safeParse(input);
  }
  return blockContentSchema.safeParse(input);
}
var quote = (value) => `'${value.replace(/\\/g, "\\\\").replace(/'/g, "\\'")}'`;
function literal(value) {
  if (typeof value === "string") return quote(value);
  if (Array.isArray(value)) return `[${value.map(literal).join(", ")}]`;
  if (value && typeof value === "object") return inlineObject(value);
  return String(value);
}
function inlineObject(value) {
  const parts = Object.entries(value).filter(([, v]) => v !== void 0).map(([k, v]) => `${k}: ${literal(v)}`);
  return parts.length === 0 ? "{}" : `{ ${parts.join(", ")} }`;
}
function blockDefToSnippet(def) {
  const lines = ["defineBlock({", `  key: ${quote(def.key)},`, `  name: ${quote(def.name)},`];
  if (def.icon !== "Cube") lines.push(`  icon: ${quote(def.icon)},`);
  if (def.description !== void 0) lines.push(`  description: ${quote(def.description)},`);
  if (def.version !== "1.0.0") lines.push(`  version: ${quote(def.version)},`);
  if (def.file !== void 0) lines.push(`  file: ${quote(def.file)},`);
  const props = Object.entries(def.props);
  if (props.length === 0) {
    lines.push("  props: {},");
  } else {
    lines.push("  props: {");
    for (const [key, prop] of props) lines.push(`    ${key}: ${inlineObject(prop)},`);
    lines.push("  },");
  }
  lines.push("})");
  return lines.join("\n");
}

// src/config.ts
import { z as z4 } from "zod";
var templateBlockSchema = z4.object({
  type: blockTypeSchema,
  locked: z4.boolean().default(false),
  content: z4.record(z4.unknown()).optional()
});
var collectionKey = z4.string().regex(/^[a-z][a-z0-9-]*$/, "Collection keys must be lowercase and hyphen-separated");
var previewInputSchema = z4.union([
  z4.string(),
  z4.object({ path: z4.string(), indirect: z4.boolean().optional() })
]);
function previewPathFor(collection, slug) {
  const path = collection?.preview?.path;
  if (path === void 0) return null;
  return path.replace(":slug", encodeURIComponent(slug));
}
var previewSchema = previewInputSchema.transform(
  (value) => typeof value === "string" ? { path: value, indirect: false } : { path: value.path, indirect: value.indirect ?? false }
);
var collectionDefSchema = z4.object({
  name: z4.string().min(1),
  icon: z4.string().default("Article"),
  description: z4.string().optional(),
  fields: z4.record(fieldKey, fieldDefSchema),
  template: z4.array(templateBlockSchema).default([]),
  // A singleton holds exactly one entry — a site-wide settings panel
  // (Team, Global config), not a list of authored documents. The server
  // enforces this (see createEntry in entry-store.ts); the admin reads it
  // to group the collection under "Settings" instead of "Content".
  singleton: z4.boolean().default(false),
  // Relabels the entry's own (intrinsic) title for the editor's big
  // heading — e.g. `titleLabel: 'Headline'`. Purely cosmetic: the entry
  // still has exactly one title column, this only changes what the
  // editor calls it.
  titleLabel: z4.string().optional(),
  preview: previewSchema.optional()
}).superRefine((collection, ctx) => {
  if (!collection.preview) return;
  if (!collection.preview.path.startsWith("/")) {
    ctx.addIssue({
      code: z4.ZodIssueCode.custom,
      path: ["preview"],
      message: `Preview path "${collection.preview.path}" must start with "/".`
    });
  }
  if (collection.singleton && collection.preview.path.includes(":slug")) {
    ctx.addIssue({
      code: z4.ZodIssueCode.custom,
      path: ["preview"],
      message: `"${collection.name}" is a singleton, so its preview path can't contain ":slug" \u2014 a singleton has no slug to substitute.`
    });
  }
});
var loopConfigSchema = z4.object({
  collections: z4.record(collectionKey, collectionDefSchema),
  blocks: z4.array(blockDefSchema).default([])
}).superRefine((config, ctx) => {
  const collectionKeys = Object.keys(config.collections);
  const seenBlockKeys = /* @__PURE__ */ new Set();
  config.blocks.forEach((block, i) => {
    if (seenBlockKeys.has(block.key)) {
      ctx.addIssue({
        code: z4.ZodIssueCode.custom,
        path: ["blocks", i, "key"],
        message: `Block key "${block.key}" is declared twice.`
      });
    }
    seenBlockKeys.add(block.key);
    if (BUILT_IN_BLOCK_TYPE_SET.has(block.key)) {
      ctx.addIssue({
        code: z4.ZodIssueCode.custom,
        path: ["blocks", i, "key"],
        message: `"${block.key}" is a built-in block type and cannot be a custom block key.`
      });
    }
    for (const [propKey, prop] of Object.entries(block.props)) {
      if (prop.type !== "collection" || !prop.source) continue;
      for (const c of prop.source.collections) {
        if (collectionKeys.includes(c)) continue;
        ctx.addIssue({
          code: z4.ZodIssueCode.custom,
          path: ["blocks", i, "props", propKey, "source", "collections"],
          message: `Block "${block.key}" binds "${propKey}" to "${c}", which is not a collection in this config.`
        });
      }
    }
  });
  for (const [key, collection] of Object.entries(config.collections)) {
    for (const [name, field] of Object.entries(collection.fields)) {
      const path = ["collections", key, "fields", name];
      if (field.type === "reference" && !collectionKeys.includes(field.to)) {
        ctx.addIssue({
          code: z4.ZodIssueCode.custom,
          path: [...path, "to"],
          message: `Reference points at "${field.to}", which is not a collection in this config.`
        });
      }
      if (field.type === "slug" && !(field.from in collection.fields)) {
        ctx.addIssue({
          code: z4.ZodIssueCode.custom,
          path: [...path, "from"],
          message: `Slug is derived from "${field.from}", which is not a field on this collection.`
        });
      }
      if (field.type === "list") {
        for (const [subName, subField] of Object.entries(field.of)) {
          if (subField.type === "list") {
            ctx.addIssue({
              code: z4.ZodIssueCode.custom,
              path: [...path, "of", subName],
              message: `List field "${name}" cannot contain a nested list ("${subName}"). Lists cannot be nested.`
            });
          }
        }
      }
    }
  }
});
function defineCollection(def) {
  return collectionDefSchema.parse(def);
}
function defineConfig(config) {
  return loopConfigSchema.parse(config);
}
function canonicalize(value) {
  if (value === void 0) return "null";
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
  if (Array.isArray(value)) return `[${value.map(canonicalize).join(",")}]`;
  const entries = Object.entries(value).filter(([, v]) => v !== void 0).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0);
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalize(v)}`).join(",")}}`;
}

export {
  FIELD_TYPES,
  fieldKey,
  fieldDefSchema,
  fields,
  valueSchemaFor,
  BLOCK_TYPES,
  blockTypeSchema,
  TEXT_BLOCK_TYPES,
  LIST_BLOCK_TYPES,
  isTextBlock,
  blockContentSchema,
  BUILT_IN_BLOCKS,
  resolveBlockMeta,
  CUSTOM_BLOCK_PREFIX,
  isCustomBlockType,
  customBlockKey,
  customBlockType,
  BLOCK_PROP_TYPES,
  blockPropDefSchema,
  blockKey,
  blockDefSchema,
  defineBlock,
  blockPropsSchemaFor,
  defaultPropsFor,
  defaultRowFor,
  blockMetaFromDef,
  customBlockContentSchema,
  BUILT_IN_BLOCK_TYPE_SET,
  parseBlockContent,
  blockDefToSnippet,
  templateBlockSchema,
  previewPathFor,
  collectionDefSchema,
  loopConfigSchema,
  defineCollection,
  defineConfig,
  canonicalize
};
